import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

/** Reserved for short confirmations — anything longer belongs in a Drawer. */
export function Modal({ open, onClose, title, description, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 animate-fade-in bg-ink/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative w-full animate-fade-in rounded-xl bg-white shadow-raised dark:bg-night-raised',
          SIZES[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pb-2 pt-4">
          <div>
            <h2 className="text-base font-semibold text-ink dark:text-slate-100">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-ink-muted dark:text-slate-400">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-ink-muted hover:bg-surface-sunken dark:hover:bg-night-sunken"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-3">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-surface-border px-5 py-3 dark:border-night-border">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
